/**
 * Laravel Echo / Reverb Configuration
 *
 * Initialises a Laravel Echo instance backed by the Reverb WebSocket server.
 * Must only run on the client (browser) side.
 *
 * Place environment variables in your .env.development / .env.production:
 *
 *   NEXT_PUBLIC_REVERB_APP_KEY=<your-reverb-app-key>
 *   NEXT_PUBLIC_REVERB_HOST=<your-reverb-host>
 *   NEXT_PUBLIC_REVERB_PORT=<your-reverb-port>
 *   NEXT_PUBLIC_REVERB_SCHEME=https          # or http
 */

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// ─── Window type augmentation ─────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EchoInstance = Echo<any>;

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: EchoInstance;
  }
}

// ─── Echo factory ─────────────────────────────────────────────────────────────

/**
 * Creates (or recreates) a Laravel Echo instance and attaches it to
 * `window.Echo`. Returns `null` when called from a server-side context.
 *
 * @param authToken  Optional Bearer token for API-token based auth.
 *                   When omitted, the function falls back to cookie-based
 *                   auth (CSRF XSRF-TOKEN cookie).
 */
export const initEcho = (authToken?: string): EchoInstance | null => {
  if (typeof window === 'undefined') return null;

  // Attach Pusher constructor so the Echo adapter can find it globally.
  window.Pusher = Pusher;

  // Build auth headers
  const authHeaders: Record<string, string> = {
    Accept: 'application/json',
  };

  if (authToken) {
    authHeaders['Authorization'] = `Bearer ${authToken}`;
  } else {
    // Cookie-based auth: extract CSRF token when present.
    const csrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    if (csrfToken) {
      authHeaders['X-XSRF-TOKEN'] = decodeURIComponent(csrfToken);
    }
  }

  const wsPort = parseInt(
    process.env['NEXT_PUBLIC_REVERB_PORT'] ?? '8082',
    10,
  );

  window.Echo = new Echo({
    broadcaster: 'reverb',
    key: process.env['NEXT_PUBLIC_REVERB_APP_KEY'] ?? 'dr8tyah8sicrewfiirj0',
    wsHost: process.env['NEXT_PUBLIC_REVERB_HOST'] ?? 'localhost',
    wsPort,
    wssPort: wsPort,
    forceTLS: process.env['NEXT_PUBLIC_REVERB_SCHEME'] === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${process.env['NEXT_PUBLIC_API_URL'] ?? 'http://localhost:8000'}/api/broadcasting/auth`,
    auth: {
      headers: authHeaders,
    },
    // Send session cookies during the auth handshake when not using a token.
    withCredentials: !authToken,
  });

  return window.Echo;
};
